import { use, useMemo, useEffect } from 'react'
import { useStore } from '@w/react-store'
import type { QueryClient, QueryOptions, QueryData } from './query-client'
import { useQueryClient } from './query-client-provider'
import type { ReactPromise } from '@w/utils-fe'
import type { Tel } from '@w/tel'

export interface UseSuspenseQueryOptions<
  TQueryFnData = unknown,
  TData = TQueryFnData,
> extends Omit<QueryOptions, 'throwOnError' | 'enabled' | 'queryFn'> {
  queryFn: (options: {
    signal: AbortSignal
    queryKey: unknown[]
    tel?: Tel
  }) => Promise<TQueryFnData>
  select?: (data: TQueryFnData) => TData
}

export interface UseSuspenseQueryResult<TData> {
  data: TData
  error: Error | undefined
  isError: boolean
  isFetching: boolean
  isRefetching: boolean
  isSuccess: boolean
  refetch: () => ReactPromise<QueryData>
  status: 'success' | 'error'
}

export function useSuspenseQuery<TQueryFnData = unknown, TData = TQueryFnData>(
  options: UseSuspenseQueryOptions<TQueryFnData, TData>,
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData> {
  const client = useQueryClient(queryClient)

  const { select, ...queryOptions } = options

  // Sync query options and get query (runs on every render)
  const query = client.syncQueryOptions({
    ...queryOptions,
    throwOnError: true,
    enabled: true,
  })

  // Subscribe to dedicated store for updates
  const state = useStore(query.getStore())

  if (state.status === 'error') {
    throw state.error ?? new Error('Query failed')
  }

  // Use React's use() hook for Suspense
  // state.promise resolves to data directly (ReactPromise handles resolved promises without suspending)
  // oxlint-disable-next-line typescript/no-non-null-assertion -- for suspense query promise is always defined
  const resolvedData = use(state.promise!)

  useEffect(() => query.subscribe(() => {}), [query])

  // Apply selector with caching
  const data = useMemo(() => {
    return select
      ? select(resolvedData as TQueryFnData)
      : (resolvedData as TData | undefined)
  }, [resolvedData, select])

  const isFetching = state.fetchStatus === 'fetching'
  const isRefetching = isFetching

  // At this point, status must be 'success' and data is guaranteed to be defined
  return {
    data: data as TData,
    error: state.error,
    isError: false,
    isFetching,
    isRefetching,
    isSuccess: true,
    refetch: query.fetch,
    status: 'success' as const,
  }
}
